import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import {
  CLOUD_API_THEME,
  cloudApiClinicalGridBg,
  cloudApiDialogCancelButtonSx,
  cloudApiDialogContentSx,
  cloudApiDialogFooterSx,
  cloudApiDialogHeaderIconSx,
  cloudApiDialogHeaderSx,
  cloudApiDialogPaperSx,
  cloudApiDialogPrimaryButtonSx,
} from '../../pages/whatsappCloudApiTheme';

export interface TwoFactorSetup {
  secret: string;
  otpauth_url: string;
  qr_code_data_url: string;
  two_factor_enabled?: boolean;
}

interface TwoFactorSetupDialogProps {
  open: boolean;
  userName: string;
  userEmail: string;
  setup: TwoFactorSetup | null;
  loading?: boolean;
  error?: string | null;
  twoFactorEnabled: boolean;
  /** Mostra a mensagem de "usuário recém-criado". */
  justCreated?: boolean;
  onClose: () => void;
  onRegenerate?: () => Promise<void> | void;
  closeLabel?: string;
}

function formatSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

const STEPS = [
  'Instale o Google Authenticator, Microsoft Authenticator ou Authy no celular.',
  'No app, toque em "+" e escolha "Ler código QR".',
  'Aponte a câmera para o QR code ao lado. O Klinvia aparece na lista com um código de 6 dígitos.',
];

const TwoFactorSetupDialog: React.FC<TwoFactorSetupDialogProps> = ({
  open,
  userName,
  userEmail,
  setup,
  loading = false,
  error,
  twoFactorEnabled,
  justCreated = false,
  onClose,
  onRegenerate,
  closeLabel = 'Concluir',
}) => {
  const [copied, setCopied] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const handleCopy = async () => {
    if (!setup) return;
    try {
      await navigator.clipboard.writeText(setup.secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const handleRegenerate = async () => {
    if (!onRegenerate) return;
    if (!confirmRegenerate) {
      setConfirmRegenerate(true);
      return;
    }
    setRegenerating(true);
    try {
      await onRegenerate();
    } finally {
      setRegenerating(false);
      setConfirmRegenerate(false);
    }
  };

  const handleClose = () => {
    setConfirmRegenerate(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: cloudApiDialogPaperSx }}>
      <DialogTitle sx={cloudApiDialogHeaderSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <ShieldIcon sx={cloudApiDialogHeaderIconSx} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
              Autenticação em dois fatores
            </Typography>
            <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate, display: 'block' }} noWrap>
              {userName} · {userEmail}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="Fechar"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ ...cloudApiDialogContentSx, pt: 2.5 }}>
        {justCreated && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            Usuário criado. O QR code já está pronto para ser lido no app autenticador.
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={twoFactorEnabled ? 'Exigido no login' : 'Não exigido no login'}
            sx={{
              fontWeight: 700,
              fontSize: '0.72rem',
              bgcolor: twoFactorEnabled ? CLOUD_API_THEME.tealSoft : CLOUD_API_THEME.mutedAlt,
              color: twoFactorEnabled ? CLOUD_API_THEME.tealDark : CLOUD_API_THEME.slate,
              border: `1px solid ${twoFactorEnabled ? CLOUD_API_THEME.borderStrong : CLOUD_API_THEME.borderSoft}`,
            }}
          />
          {!twoFactorEnabled && (
            <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate }}>
              Para exigir o código, ative em Editar usuário.
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {loading || !setup ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            {loading ? <CircularProgress size={32} sx={{ color: CLOUD_API_THEME.teal }} /> : null}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2.5,
              alignItems: { xs: 'center', sm: 'flex-start' },
            }}
          >
            <Box
              sx={{
                ...cloudApiClinicalGridBg,
                p: 1.5,
                borderRadius: 3,
                border: `1px solid ${CLOUD_API_THEME.border}`,
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={setup.qr_code_data_url}
                alt="QR code para o aplicativo autenticador"
                sx={{ width: 188, height: 188, display: 'block', borderRadius: 1.5, bgcolor: '#fff', p: 0.75 }}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box component="ol" sx={{ m: 0, pl: 2.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {STEPS.map((step) => (
                  <Typography key={step} component="li" variant="body2" sx={{ color: CLOUD_API_THEME.slateMid, fontSize: '0.83rem' }}>
                    {step}
                  </Typography>
                ))}
              </Box>

              <Typography variant="caption" sx={{ mt: 2, display: 'block', color: CLOUD_API_THEME.slate, fontWeight: 600 }}>
                Sem câmera? Digite esta chave no app (tipo: baseado em tempo)
              </Typography>
              <Box
                sx={{
                  mt: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 2,
                  bgcolor: CLOUD_API_THEME.tealSoft,
                  border: `1px dashed ${CLOUD_API_THEME.borderStrong}`,
                  borderLeft: `3px solid ${CLOUD_API_THEME.teal}`,
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: CLOUD_API_THEME.navy,
                    wordBreak: 'break-all',
                  }}
                >
                  {formatSecret(setup.secret)}
                </Typography>
                <Tooltip title={copied ? 'Copiado' : 'Copiar chave'}>
                  <IconButton size="small" onClick={handleCopy} sx={{ color: CLOUD_API_THEME.tealDark }}>
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        )}

        {confirmRegenerate && (
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            O QR code atual deixará de funcionar. O usuário precisará ler o novo código no app. Clique de novo para confirmar.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ ...cloudApiDialogFooterSx, justifyContent: 'space-between' }}>
        {onRegenerate ? (
          <Button
            onClick={handleRegenerate}
            disabled={loading || regenerating}
            startIcon={regenerating ? <CircularProgress size={14} /> : <RefreshIcon />}
            sx={{ ...cloudApiDialogCancelButtonSx, color: confirmRegenerate ? '#b45309' : undefined }}
          >
            {confirmRegenerate ? 'Confirmar novo QR code' : 'Gerar novo QR code'}
          </Button>
        ) : <span />}
        <Button variant="contained" onClick={handleClose} sx={cloudApiDialogPrimaryButtonSx}>
          {closeLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TwoFactorSetupDialog;
